import React, { useMemo } from 'react';

/**
 * Sparkline — pure SVG inline trend visual.
 *
 * No chart library — for tiny inline trend lines we keep the bundle
 * lean by drawing the path ourselves. Two variants: line (with
 * optional area fill) and bar.
 *
 * Auto-scales to the container width via viewBox + preserveAspectRatio,
 * so the same component works in a 240-px hero card slot or a
 * full-width row.
 *
 * @param {Object} props
 * @param {number[]} props.data
 *   Numeric series. Empty / single-point series renders nothing.
 * @param {'line'|'bar'} [props.variant='line']
 * @param {string}       [props.tone='action']
 *   Token name suffix — maps to var(--v2-{tone}) for stroke/fill.
 * @param {boolean}      [props.fillArea=true]
 *   Line variant only. Whether to draw the soft fill below the line.
 * @param {number}       [props.height=28]
 * @param {string}       [props.className]
 * @param {string}       [props.ariaLabel]
 */
export function Sparkline({
  data,
  variant = 'line',
  tone = 'action',
  fillArea = true,
  height = 28,
  className = '',
  ariaLabel,
}) {
  const W = 100;     // viewBox width
  const H = 30;      // viewBox height
  const PADDING = 1; // 1-unit padding so stroke isn't clipped

  const TONE_STROKE = {
    action:  'var(--v2-action)',
    success: 'var(--v2-success)',
    warning: 'var(--v2-warning)',
    danger:  'var(--v2-danger)',
    brand:   'var(--v2-brand)',
    trust:   'var(--v2-accent-trust)',
    cyan:    'var(--v2-accent-cyan)',
  };

  const TONE_FILL = {
    action:  'var(--v2-action-soft)',
    success: 'var(--v2-success-soft)',
    warning: 'var(--v2-warning-soft)',
    danger:  'var(--v2-danger-soft)',
    brand:   'var(--v2-brand-soft)',
    trust:   'var(--v2-accent-trust-soft)',
    cyan:    'var(--v2-accent-cyan-soft)',
  };

  const stroke = TONE_STROKE[tone] || TONE_STROKE.action;
  const fill = TONE_FILL[tone] || TONE_FILL.action;

  const { linePath, areaPath, bars } = useMemo(() => {
    if (!data || data.length < 2) return { linePath: '', areaPath: '', bars: [] };
    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;

    const xs = data.map((_, i) => PADDING + (i / (data.length - 1)) * (W - PADDING * 2));
    const ys = data.map((v) => H - PADDING - ((v - min) / range) * (H - PADDING * 2));

    const points = xs.map((x, i) => `${x.toFixed(2)},${ys[i].toFixed(2)}`);
    const linePath = `M ${points.join(' L ')}`;
    const areaPath = `${linePath} L ${xs[xs.length - 1].toFixed(2)},${H} L ${xs[0].toFixed(2)},${H} Z`;

    // Bar variant: equal-width bars, height proportional
    const barW = (W - PADDING * 2) / data.length * 0.7;
    const barGap = (W - PADDING * 2) / data.length * 0.3;
    const bars = data.map((v, i) => {
      const h = ((v - min) / range) * (H - PADDING * 2);
      return {
        x: PADDING + i * (barW + barGap),
        y: H - PADDING - h,
        w: barW,
        h,
      };
    });

    return { linePath, areaPath, bars };
  }, [data]);

  if (!data || data.length < 2) return null;

  return (
    <svg
      role="img"
      aria-label={ariaLabel || 'Trend over time'}
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      className={'block ' + className}
      style={{ width: '100%', height }}
    >
      {variant === 'line' && fillArea && (
        <path d={areaPath} fill={fill} />
      )}
      {variant === 'line' && (
        <path
          d={linePath}
          fill="none"
          stroke={stroke}
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      )}
      {variant === 'bar' && bars.map((b, i) => (
        <rect
          key={i}
          x={b.x.toFixed(2)}
          y={b.y.toFixed(2)}
          width={b.w.toFixed(2)}
          height={b.h.toFixed(2)}
          fill={stroke}
          opacity={0.85}
          rx={0.5}
        />
      ))}
    </svg>
  );
}

export default Sparkline;
